import { useCallback, useEffect } from "react";
import logger from "@/lib/logging/logger";
import type {
	InboundNativeMessage,
	InboundNativeMessageName,
	InboundNativeMessageTypes,
	NativePongPayload,
	NativeThermalState,
	OutboundNativeMessageName,
	OutboundNativeMessageTypes,
} from "./nativeMessages";

const WEBVIEW_EVENT_NAME = "webview-host-event";

/** Injected at document start by wrapper builds that speak the native bridge contract. */
export type PandasNativeBridge = {
	version: string;
	platform: string;
	capabilities: string[];
	/** Wrapper builds from 18 also expose the managed camera mode and a live thermal state. */
	cameraMode?: string;
	thermal?: NativeThermalState;
	post: (message: string, payload?: unknown) => void;
};

declare global {
	interface Window {
		webkit?: {
			messageHandlers: {
				iosListener: {
					postMessage: (msg: unknown) => void;
				};
			};
		};
		receiveMessageFromiOS?: (msg: unknown) => void;
		PandasNative?: PandasNativeBridge;
	}
}

export function sendOutboundNativeMessage<T extends OutboundNativeMessageName>(
	message: T,
	payload: OutboundNativeMessageTypes[T],
) {
	if (typeof window === "undefined") return;
	if (typeof window.PandasNative?.post === "function") {
		window.PandasNative.post(message, payload);
	} else if (window.webkit?.messageHandlers?.iosListener) {
		window.webkit.messageHandlers.iosListener.postMessage({ message, payload });
	} else {
		logger.warning("INFO", "iOS handler not available");
	}
}

let unreadableEventLogged = false;

const previewOf = (detail: unknown): string => {
	try {
		return (typeof detail === "string" ? detail : JSON.stringify(detail)).slice(0, 120);
	} catch {
		return Object.prototype.toString.call(detail);
	}
};

// The contract promises `{ message, payload }` as the event detail; a wrapper build that ships
// it as a JSON string is read the same way rather than dropped silently.
const readNativeEventDetail = (event: Event): unknown => {
	const detail = (event as { detail?: unknown }).detail;
	if (typeof detail !== "string") return detail;
	try {
		return JSON.parse(detail);
	} catch {
		return detail;
	}
};

export function addInboundNativeMessageListener(listener: (message: InboundNativeMessage) => void): () => void {
	const onNative = (event: Event) => {
		const detail = readNativeEventDetail(event);
		const message =
			typeof detail === "object" && detail !== null && "message" in detail
				? (detail as { message: unknown }).message
				: undefined;
		if (typeof message !== "string") {
			if (!unreadableEventLogged) {
				unreadableEventLogged = true;
				logger.warning("INFO", "Native event with an unreadable shape", {
					detailType: detail === null ? "null" : typeof detail,
					preview: previewOf(detail),
				});
			}
			return;
		}
		const payload = (detail as { payload?: unknown }).payload;
		try {
			listener({ message, payload } as InboundNativeMessage);
		} catch (error) {
			logger.warning("INFO", "Native message listener error", error);
		}
	};
	window.addEventListener(WEBVIEW_EVENT_NAME, onNative);
	return () => window.removeEventListener(WEBVIEW_EVENT_NAME, onNative);
}

function useNativeMessages(handler: (msg: unknown) => void) {
	useEffect(() => {
		const onNative = (e: CustomEvent) => {
			try {
				handler(e?.detail);
			} catch (error) {
				logger.warning("INFO", "onNative error", error);
			}
		};

		window?.addEventListener(WEBVIEW_EVENT_NAME, onNative as EventListener);

		return () => {
			window?.removeEventListener(WEBVIEW_EVENT_NAME, onNative as EventListener);
		};
	}, [handler]);
}

export function useInboundNativeMessages<T extends InboundNativeMessageName>(
	message: T,
	handler: (msg: InboundNativeMessageTypes[T]) => void,
) {
	const ignoreOtherMessagesWrapper = useCallback(
		(msg: unknown) => {
			try {
				const nativeMessage = msg as { message?: InboundNativeMessageName; payload?: InboundNativeMessageTypes[T] };
				if (nativeMessage?.message === message && nativeMessage.payload !== undefined) {
					handler(nativeMessage.payload);
				}
			} catch (error) {
				logger.warning("INFO", "handler error", error);
			}
		},
		[message, handler],
	);

	useNativeMessages(ignoreOtherMessagesWrapper);
}

export function useTestNativeConnection(callback: (msg: NativePongPayload) => void) {
	useInboundNativeMessages("pong", callback);

	useEffect(() => {
		try {
			sendOutboundNativeMessage("ping", undefined);
		} catch (error) {
			logger.warning("INFO", "sendOutboundNativeMessage error", error);
		}
	}, []);
}
